/* eslint-disable react-hooks/exhaustive-deps */
import { Link } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { pushOneCityGuide } from "../../../middlewares/sysNavigation/SysNavigation";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import {
  Modal,
  GoBack,
  MyButton,
  MySearch,
  Pagination,
  DashboardIntro,
} from "../../home/component/Component";
import { _TYPES_ } from "../utils/types";
import { _PAGE_SIZE_ } from "../../../middlewares/other/tools";
import { useGeoPositionContext } from "../utils/geoPosition";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import PromoteContent from "../utils/PromoteContent";
import { FaRegTrashAlt } from "react-icons/fa";
import { RiRefreshLine } from "react-icons/ri";

/* Routes */
const addRoute = "/data/city/guide/add";
const editRoute = "/data/city/guide/edit";
const doseeDataRoutes = "/dosee/data";

const Overview = () => {
  const { cityGuides, cityGuidesCount, fetchDoSeeData, deleteOneContent } =
    useDoSeeDataContext();

  const [cityGuidesList, setCityGuidesList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);
  const [localSearch, setLocalSearch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContentIds, setSelectedContentIds] = useState([]);

  const {
    country_id,
    entry_label,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const data_request_body = {
    country_id,
    first_level_id,
    second_level_id,
    third_level_id,
    fourth_level_id,
    content: _TYPES_.CITYGUIDE,
  };

  useEffect(() => {
    if (!cityGuides) fetchDoSeeData(data_request_body, page, pageSize);
    setCityGuidesList(cityGuides || []);
  }, [cityGuides]);

  const reloadData = () => fetchDoSeeData(data_request_body, page, pageSize);

  const SearchList = (e) => {
    const key = e.target.value.toString().toLowerCase();
    if (!key) {
      setCityGuidesList(cityGuides || []);
      return;
    }
    const searchArray = (cityGuides || []).filter((ctg) =>
      (ctg?.title || "").toString().toLowerCase().includes(key)
    );
    setCityGuidesList(searchArray);
  };

  const LocalData = (localSearch?.length > 0 ? localSearch : cityGuidesList) || [];

  /* ---------- Select All: only current page/filtered rows ---------- */
  const visibleIds = useMemo(
    () => LocalData.map((cg) => cg?.city_guide_id).filter(Boolean),
    [LocalData]
  );

  // Keep selection in sync with what’s visible (removes selections no longer in view)
  useEffect(() => {
    if (selectedContentIds.length === 0) return;
    setSelectedContentIds((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds.length, page, pageSize]);

  const allSelected = visibleIds.length > 0 && selectedContentIds.length === visibleIds.length;
  const someSelected =
    selectedContentIds.length > 0 && selectedContentIds.length < visibleIds.length;

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      // Unselect all visible
      setSelectedContentIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      // Add all visible (merge)
      setSelectedContentIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleOne = (id) => {
    setSelectedContentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ---------- Bulk delete ---------- */
  const deleteMany = async () => {
    if (selectedContentIds.length === 0) return;
    setLoading(true);
    for (let i = 0; i < selectedContentIds.length; i++) {
      await deleteOneContent({
        data_id: selectedContentIds[i],
        content: _TYPES_.CITYGUIDE,
      });
    }
    setSelectedContentIds([]);
    setLoading(false);
    reloadData();
  };

  /* ---------- UI shells ---------- */
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-5";

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        <BreadCrumbComponent />
        <GoBack props={{ route: doseeDataRoutes }} />

        <div className="my-3">
          <DashboardIntro props={{ intro: `City Guides`, name: entry_label }} />
        </div>

        <div className={`${card} ${sectionPad} mb-4`}>
          <MySearch
            type={_TYPES_.CITYGUIDE}
            value={localSearch}
            showGlobalSearch={true}
            setValue={setLocalSearch}
            onChange={SearchList}
          />

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <div className="flex items-center gap-3">
              {/* Select-all (indeterminate supported) */}
              <label className="inline-flex items-center gap-2 text-sm text-slate-800">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                {allSelected ? "Unselect all (visible)" : "Select all (visible)"}
              </label>

              <MyButton
                onClick={deleteMany}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${selectedContentIds.length === 0 || loading
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-50 text-slate-800 border-slate-200"
                  }`}
              >
                {loading ? (
                  <RiRefreshLine className="text-slate-700 text-lg animate-spin" />
                ) : (
                  <FaRegTrashAlt className="text-red-600 text-base" />
                )}
                Delete {selectedContentIds.length || 0}
              </MyButton>
            </div>

            {country_id ? (
              <Link to={addRoute}>
                <MyButton className="py-2.5 px-4 rounded-lg bg-blue-700 hover:bg-blue-600 text-white">
                  Add City Guide
                </MyButton>
              </Link>
            ) : null}
          </div>
        </div>

        {/* Table */}
        <div className={`${card} ${sectionPad} mb-4`}>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left text-slate-600">
                  <th className="px-3 py-2 w-[60px]">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-3 py-2 min-w-[220px] font-semibold">Title</th>
                  <th className="px-3 py-2 min-w-[160px] font-semibold">Start Date</th>
                  <th className="px-3 py-2 min-w-[160px] text-center font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {LocalData.length > 0 ? (
                  LocalData.map((item) => {
                    const id = item?.city_guide_id;
                    const selected = selectedContentIds.includes(id);
                    return (
                      <tr key={id} className={selected ? "bg-blue-50/40" : ""}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleOne(id)}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2">{item?.title}</td>
                        <td className="px-3 py-2">{item?.stcityguide_date}</td>
                        <td className="px-3 py-2">
                          <div className="flex justify-center">
                            <ActionButton props={item} reloadData={reloadData} />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="px-3 py-6 text-slate-400" colSpan={4}>
                      No city guides found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Pagination
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total_items={cityGuidesCount}
          callbackFunction={(p, size) => {
            fetchDoSeeData(data_request_body, p, size);
          }}
        />
      </div>
    </div>
  );
};

/**
 *  @author Ntavigwa Bashombe
 */
const ActionButton = ({ props, reloadData }) => {
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);
  const { cityGuides, deleteOneContent, updateOneContent } = useDoSeeDataContext();

  const [showModal, setShowModal] = useState(false);
  const [promote, setPromote] = useState(false);
  const [promoteLoader, setPromoteLoader] = useState(false);

  const city_guide_id = props?.city_guide_id;
  const cityguide_title = props?.title;
  const cityguide = props;

  const deleteOneCityGuideById = async (id) => {
    const { status } = await deleteOneContent({
      data_id: id,
      content: _TYPES_.CITYGUIDE,
    });
    setShowModal(false);
    if (status === 200) reloadData();
  };

  const buildData = (object, position) => ({
    promoted: `${position}`,
    country_id: object?.country_id,
    description: object?.description,
    email_address: object?.email_address,
    first_level_id: object?.first_level_id,
    fourth_level_id: object?.fourth_level_id,
    second_level_id: object?.second_level_id,
    slug: object?.slug,
    status: object?.status,
    third_level_id: object?.third_level_id,
    thumbnail: object?.thumbnail,
    title: object?.title,
    url_images: object?.url_images,
  });

  const promotion = async (position) => {
    const previousPromoted = cityGuides?.find(
      (x) => x?.promoted === String(position)
    );

    setPromoteLoader(true);

    const { status } = await updateOneContent({
      data_id: cityguide?.city_guide_id,
      data: buildData(props, position),
      content: _TYPES_.CITYGUIDE,
    });

    if (previousPromoted) {
      await updateOneContent({
        // FIX: correct id field when un-promoting the previous one
        data_id: previousPromoted?.city_guide_id,
        data: buildData(previousPromoted, "no"),
        content: _TYPES_.CITYGUIDE,
      });
    }

    setPromoteLoader(false);
    if (status === 200) reloadData();
  };

  const suspension = async (item) => {
    const data = {
      country_id: item?.country_id,
      description: item?.description,
      first_level_id: item?.first_level_id,
      fourth_level_id: item?.fourth_level_id,
      second_level_id: item?.second_level_id,
      slug: item?.slug,
      status: item?.status === 1 ? 0 : 1,
      third_level_id: item?.third_level_id,
      thumbnail: item?.thumbnail,
      title: item?.title,
      url_images: item?.url_images,
    };

    const { status } = await updateOneContent({
      data_id: item?.city_guide_id,
      data,
      content: _TYPES_.CITYGUIDE,
    });

    if (status === 200) reloadData();
  };

  return (
    <>
      <Modal
        props={{
          name: cityguide_title,
          showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneCityGuideById(city_guide_id),
        }}
      />

      <PromoteContent
        visible={promote}
        setVisible={setPromote}
        loading={promoteLoader}
        ids={cityGuides
          ?.filter(
            (x) => !["yes", "no", null, undefined].includes(x?.promoted)
          )
          .map((x) => ({
            id: x?.city_guide_id,
            title: x?.title,
            position: parseInt(x?.promoted, 10),
          }))}
        onClick={(position) => promotion(position)}
      />

      <div className="flex flex-row justify-end items-center px-2 py-2 gap-2">
        {isSuperAdmin ? (
          <>
            <MyButton
              title={cityguide?.status === 1 ? "Suspend" : "Unsuspend"}
              className={`py-2 px-4 rounded-lg ${cityguide?.status === 1
                ? "bg-slate-800 hover:bg-slate-700"
                : "bg-amber-700 hover:bg-amber-600"
                } text-white`}
              onClick={() => suspension(cityguide)}
            />
            <MyButton
              title={
                ["yes", "no", null, undefined].includes(cityguide?.promoted)
                  ? "Promote"
                  : "Unpromote"
              }
              className={`py-2 px-4 rounded-lg ${["yes", "no", null, undefined].includes(cityguide?.promoted)
                ? "bg-slate-800 hover:bg-slate-700"
                : "bg-amber-700 hover:bg-amber-600"
                } text-white`}
              onClick={() => setPromote(true)}
            />
          </>
        ) : null}

        <Link to={editRoute}>
          <MyButton
            title="Edit"
            className="py-2 px-4 rounded-lg bg-blue-700 hover:bg-blue-600 text-white"
            onClick={() => pushOneCityGuide(cityguide)}
          />
        </Link>
        <MyButton
          title="Delete"
          className="py-2 px-4 rounded-lg bg-red-700 hover:bg-red-600 text-white"
          onClick={() => setShowModal(true)}
        />
      </div>
    </>
  );
};

export default Overview;
